/**
 * PositioningCard — smart-money positioning read for the Macro page.
 *
 * Surfaces /api/macro/positioning: CFTC hedge-fund (leveraged) vs asset-manager
 * extremes, NY Fed SOMA liquidity, and Treasury TIC foreign flows, synthesised
 * into a net risk-on/off bias. Self-fetching; renders nothing until loaded and
 * hides itself if the endpoint errors (the rest of the Macro page is unaffected).
 */
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { fmtSigned } from '@/lib/format'

type Item = { summary: string | null; sentiment: number | null }
type Positioning = {
  net_bias: { label: string; score: number; rationale: string } | null
  components: {
    hedge_fund_positioning: number | null
    fed_liquidity: number | null
    foreign_flows: number | null
  } | null
  positioning: Item[] | null
  liquidity: Item[] | null
  foreign_demand: Item[] | null
  commodity_positioning: Item[] | null
  dealer_positioning: Item[] | null
}

// Thin alias to the shared null-safe formatter (also adds NaN/Infinity safety).
export const fmt = (n: number | null | undefined) => fmtSigned(n, 2)

export function biasClasses(label: string): string {
  if (label === 'risk-on') return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/50'
  if (label === 'risk-off') return 'bg-rose-500/20 text-rose-200 border-rose-500/50'
  return 'bg-zinc-500/20 text-zinc-100 border-zinc-500/50'
}

function Component({ label, value }: { label: string; value: number | null | undefined }) {
  const tone = value == null ? 'text-text-muted' : value > 0 ? 'text-emerald-300' : value < 0 ? 'text-rose-300' : 'text-zinc-200'
  return (
    <div className="flex-1 min-w-[120px] rounded-lg bg-black/20 border border-border px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`text-sm font-mono font-semibold ${tone}`}>{fmt(value)}</div>
    </div>
  )
}

export default function PositioningCard() {
  const [data, setData] = useState<Positioning | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    api.get('/api/macro/positioning')
      .then((res) => { if (!cancelled) setData(res.data as Positioning) })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [])

  if (failed || !data) return null

  const bias = data.net_bias
  const comps = data.components
  const extremes = (data.positioning ?? []).filter((p) => p.summary)
  const commodities = (data.commodity_positioning ?? []).filter((p) => p.summary)
  const dealers = (data.dealer_positioning ?? []).filter((p) => p.summary)

  return (
    <div className="rounded-xl bg-surface-card border border-border p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-sm font-semibold text-text-primary">Smart-Money Positioning</h2>
        {bias && (
          <span className={`px-2.5 py-1 rounded-lg border text-xs font-semibold ${biasClasses(bias.label)}`}>
            {bias.label.toUpperCase()} · {fmt(bias.score)}
          </span>
        )}
      </div>

      {bias?.rationale && (
        <p className="text-xs text-text-secondary">{bias.rationale}</p>
      )}

      <div className="flex gap-2 flex-wrap">
        <Component label="Hedge funds" value={comps?.hedge_fund_positioning} />
        <Component label="Fed liquidity" value={comps?.fed_liquidity} />
        <Component label="Foreign flows" value={comps?.foreign_flows} />
      </div>

      {extremes.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-text-muted">Positioning extremes (CFTC TFF)</div>
          {extremes.slice(0, 6).map((p, i) => (
            <div key={i} className="text-[11px] text-text-secondary leading-snug">{p.summary}</div>
          ))}
        </div>
      )}

      {commodities.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-text-muted">Commodity positioning (CFTC CoT)</div>
          {commodities.slice(0, 6).map((p, i) => (
            <div key={i} className="text-[11px] text-text-secondary leading-snug">{p.summary}</div>
          ))}
        </div>
      )}

      {dealers.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-text-muted">Primary-dealer positioning (NY Fed)</div>
          {dealers.slice(0, 6).map((p, i) => (
            <div key={i} className="text-[11px] text-text-secondary leading-snug">{p.summary}</div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-text-muted">
        Source: CFTC TFF + CoT · NY Fed SOMA + primary dealers · Treasury TIC
      </p>
    </div>
  )
}
